/**
 * Sign in with a six-digit code.
 *
 * An address Calorio does not admit is told so on a screen of its own,
 * instead of being asked for a code that will never arrive: Authgate answers
 * `/auth/code/request` with 403 for it. Beyond that the UI must not guess:
 *
 * - **Never say whether a code was delivered.** Any other answer, rate limiting
 *   included, means only "if that address can sign in, a code is on its way".
 * - **Never distinguish wrong from expired from already-used.** Verification
 *   answers with one message for all of them, and inventing a friendlier
 *   distinction here would leak what the server withheld on purpose.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";

import { useSession } from "../app/session";
import { describePasskeyError, passkeysEnabled, passkeysSupported } from "../lib/passkeys";

/**
 * How long a code is good for, in Authgate's gift rather than ours:
 * `authgate.code.ttl=10m`, in the auth-service repo at
 * `src/main/resources/application.properties`. Named here because it belongs to
 * another service, so whoever finds this figure wrong knows where it moved.
 */
const CODE_MINUTES = 10;

/**
 * Seconds between code requests. Authgate allows five per address per hour
 * (`authgate.ratelimit.code-requests-per-email-per-hour`, same file), and a
 * refused request answers exactly like an accepted one — so a spent budget is
 * invisible from here, and the only protection is not letting it be spent by
 * tapping. A minute is longer than mail normally takes, which makes the first
 * press mean something, and it spreads the hour's five requests across four
 * minutes instead of four seconds while leaving all of them inside the ten
 * the first code is alive.
 */
const RESEND_SECONDS = 60;

export function SignIn() {
  const { expired, requestCode, verifyCode, passkeySignIn } = useSession();

  // Offered only when this browser can and Calorio has passkeys switched on,
  // so the button never leads somewhere that cannot work.
  const [passkeys, setPasskeys] = useState(false);
  useEffect(() => {
    if (!passkeysSupported()) return;
    let cancelled = false;
    void passkeysEnabled().then((enabled) => {
      if (!cancelled) setPasskeys(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const [stage, setStage] = useState<"email" | "code" | "not-invited">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  // A problem is shown under the control it came from: a failed passkey under
  // the passkey button, not under an email field that played no part in it.
  const [problem, setProblem] = useState<{ source: "passkey" | "form"; text: string } | null>(
    null,
  );
  const passkeyProblem = problem?.source === "passkey" ? problem.text : null;
  const formProblem = problem?.source === "form" ? problem.text : null;

  // The passkey ceremony, while the browser's prompt is up. Separate from
  // `busy` so the page can say what it is waiting for, and so the person can
  // take it back: the prompt otherwise stays until answered or timed out, and
  // the email route is locked behind it the whole time.
  const [waiting, setWaiting] = useState(false);
  const ceremony = useRef<AbortController | null>(null);
  const emailField = useRef<HTMLInputElement>(null);
  const locked = busy || waiting;

  // Seconds until another code may be asked for, and whether one already has
  // been. `sends` counts requests only so that each one restarts the clock
  // below: a resend is a fresh wait, not a continuation of the last.
  const [countdown, setCountdown] = useState(RESEND_SECONDS);
  const [resent, setResent] = useState(false);
  const [sends, setSends] = useState(0);

  // One interval per send, owned by the effect: leaving the code stage — by
  // signing in, by "Use a different address", or by unmounting — takes the
  // cleanup with it, so nothing is left ticking behind the email field. The
  // closure keeps its own count, so the interval is never torn down and
  // rebuilt mid-countdown and can stop itself at zero.
  useEffect(() => {
    if (stage !== "code") return;
    setCountdown(RESEND_SECONDS);
    let left = RESEND_SECONDS;
    const tick = setInterval(() => {
      left -= 1;
      setCountdown(left);
      if (left <= 0) clearInterval(tick);
    }, 1000);
    return () => clearInterval(tick);
  }, [stage, sends]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setProblem(null);

    if (stage === "email") {
      if (!email.includes("@")) {
        return setProblem({ source: "form", text: "That doesn't look like an email address." });
      }
      setBusy(true);
      try {
        setStage((await requestCode(email)) === "not-invited" ? "not-invited" : "code");
      } catch {
        setProblem({ source: "form", text: "Couldn't reach the server. Check your connection." });
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    try {
      // One message for wrong, expired, consumed and exhausted alike.
      if (!(await verifyCode(email, code))) {
        setProblem({ source: "form", text: "That code is not valid." });
      }
    } catch {
      setProblem({ source: "form", text: "Couldn't reach the server. Check your connection." });
    } finally {
      setBusy(false);
    }
  };

  // The commonest way this screen goes wrong is mail that is slow or a code
  // that went stale while it sat in an inbox, and until now the only way out
  // was a button that says it is for a different address.
  const resend = async () => {
    setProblem(null);
    setBusy(true);
    try {
      // The same answer means the same thing it did the first time: a 403 says
      // the address cannot sign in, and anything else says only that a code
      // may be on its way.
      if ((await requestCode(email)) === "not-invited") {
        setStage("not-invited");
        return;
      }
      setResent(true);
      setSends((n) => n + 1);
    } catch {
      // Nothing left, so nothing to wait for: the button stays pressable.
      setProblem({ source: "form", text: "Couldn't reach the server. Check your connection." });
    } finally {
      setBusy(false);
    }
  };

  const signInWithAPasskey = async () => {
    setProblem(null);
    const controller = new AbortController();
    ceremony.current = controller;
    setWaiting(true);
    try {
      await passkeySignIn(controller.signal);
    } catch (error) {
      const name = (error as { name?: string } | null)?.name;
      // Taken back from the Cancel link below: a choice, nothing to report.
      if (name === "AbortError") return;
      setProblem({
        source: "passkey",
        text:
          describePasskeyError(error) +
          // Cancelled or no passkey here: the other route is right there, and
          // a passkey for next time is made from inside the account.
          (name === "NotAllowedError"
            ? " Get a code by email below, then add a passkey from the You screen."
            : ""),
      });
      emailField.current?.focus();
    } finally {
      if (ceremony.current === controller) ceremony.current = null;
      setWaiting(false);
    }
  };

  const cancelPasskey = () => {
    ceremony.current?.abort();
    emailField.current?.focus();
  };

  const startOver = () => {
    setStage("email");
    setCode("");
    setProblem(null);
    // A different address starts a different wait; the old confirmation was
    // about the old address.
    setResent(false);
  };

  if (stage === "not-invited") {
    return (
      <div className="signin">
        <div className="signin__form stack" role="alert">
          <h1 className="signin__brand">Calorio</h1>
          <p style={{ margin: 0 }}>
            <strong>{email}</strong> isn't invited to Calorio.
          </p>
          <p className="muted" style={{ margin: 0 }}>
            Only invited addresses can sign in, so no code was sent. Ask whoever
            invited you which address they used, or ask them for an invitation.
          </p>
          <button className="button" type="button" onClick={startOver} autoFocus>
            Use a different address
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="signin">
      <form className="signin__form stack" onSubmit={submit}>
        <h1 className="signin__brand">Calorio</h1>

        {/* Why this screen appeared under somebody who was using the app a
            moment ago. Quiet rather than red: a session running out is the
            normal end of a session, not a failure on their part, and the two
            things they will want to know are that it is a sign-in they need and
            that the morning's entries are still there. Only while there is
            still an address to give — once a code is on its way, the screen has
            a task of its own and this would be in the way of it. */}
        {expired && stage === "email" && (
          <p className="signin__expired" role="status">
            Your session expired. Sign in again to pick up where you left off —
            everything you logged is still here.
          </p>
        )}

        <p className="muted" style={{ margin: 0 }}>
          {stage !== "email"
            ? `We've sent a code to ${email}.`
            : passkeys
              ? "Sign in with a passkey, or get a six-digit code by email."
              : "Sign in and we'll email you a six-digit code."}
        </p>

        {/* How long the code lasts is the one thing nobody can tell from the
            outside, and it decides whether waiting another minute is sensible
            or pointless. See CODE_MINUTES for whose setting this is. */}
        {stage === "code" && (
          <p className="signin__hint">
            The code works for {CODE_MINUTES} minutes from when you asked for it.
          </p>
        )}

        {/*
          The passkey comes first and as a full button, with an "or" between it
          and the email field: the two are alternatives. A passkey sign-in is
          usernameless — Authgate never sees the address, the browser picks the
          passkey — so nothing may suggest that typing an address is a step on
          the way to it. It used to be a text link under the Send code button,
          where it read as exactly that.
        */}
        {stage === "email" && passkeys && (
          <>
            <div className="signin__passkey">
              <button
                type="button"
                className={`button button--secondary${waiting ? " is-waiting" : ""}`}
                onClick={() => void signInWithAPasskey()}
                disabled={locked}
              >
                <svg
                  aria-hidden="true"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="8" cy="12" r="4" />
                  <path d="M12 12h9" />
                  <path d="M18 12v3" />
                  <path d="M15 12v2" />
                </svg>
                {waiting
                  ? "Waiting for your device…"
                  : passkeyProblem
                    ? "Try the passkey again"
                    : "Sign in with a passkey"}
              </button>
              {waiting ? (
                <p className="signin__hint" role="status">
                  Choose a Calorio passkey in the prompt that just opened. The address below
                  is not used.
                </p>
              ) : passkeyProblem ? (
                <div className="signin__callout" role="alert">
                  <svg
                    aria-hidden="true"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v5" />
                    <path d="M12 16h.01" />
                  </svg>
                  <span>{passkeyProblem}</span>
                </div>
              ) : (
                <p className="signin__hint">
                  Uses the passkey you choose on this device. No address needed.
                </p>
              )}
            </div>
            <div className="signin__or" aria-hidden="true">
              <span>or</span>
            </div>
          </>
        )}

        {stage === "email" ? (
          <div className={`signin__email${waiting ? " is-muted" : ""}`}>
            {passkeys && (
              <label className="signin__label" htmlFor="signin-email">
                Get a code by email
              </label>
            )}
            <input
              ref={emailField}
              id="signin-email"
              className="field"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              aria-label="Email address"
              autoComplete="email"
              autoFocus
              disabled={locked}
            />
          </div>
        ) : (
          <input
            className="field num signin__code"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            aria-label="Six-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            disabled={busy}
          />
        )}

        {formProblem && <p className="error" style={{ margin: 0 }}>{formProblem}</p>}

        <button
          className="button"
          type="submit"
          disabled={locked || (stage === "code" && code.length < 6)}
        >
          {busy ? "…" : stage === "email" ? "Send code" : "Sign in"}
        </button>

        {/* Another code first, a different address second. Slow mail and stale
            codes are what actually happens here, and the fix for both was
            hidden behind a label that denies doing it. The countdown sits in
            the button's own words, because a control that cannot be pressed
            should say when it can be. */}
        {stage === "code" && (
          <>
            <button
              type="button"
              className="signin__secondary"
              onClick={() => void resend()}
              disabled={locked || countdown > 0}
            >
              {countdown > 0 ? `Send another code in ${countdown}s` : "Send another code"}
            </button>
            {/* Conditional, like the first send: rate limiting answers the same
                as success, so "sent" is more than we know. That the newer code
                wins is the one thing worth saying, for the inbox that ends up
                holding two. */}
            {resent && (
              <p className="signin__hint" role="status">
                If that address can sign in, another code is on its way. Use the newest
                one — a new code replaces the one before it.
              </p>
            )}
            <button type="button" className="signin__secondary" onClick={startOver}>
              Use a different address
            </button>
          </>
        )}

        {waiting && (
          <button type="button" className="signin__secondary" onClick={cancelPasskey}>
            Cancel and use email instead
          </button>
        )}
      </form>
    </div>
  );
}
